<template>
  <div class="usage-reference-lists">
    <div v-for="group in groups" :key="group.key" class="usage-reference-lists__group">
      <p v-if="group.title" class="usage-reference-lists__title">{{ group.title }}</p>
      <ul class="usage-reference-lists__list">
        <li v-for="item in group.items" :key="item.id || item.name" class="usage-reference-lists__item">
          <fs-router-link v-if="item.path" :to="item.path" class="usage-reference-lists__link">
            {{ item.name }}<template v-if="item.id"> (#{{ item.id }})</template>
          </fs-router-link>
          <template v-else
            >{{ item.name }}<template v-if="item.id"> (#{{ item.id }})</template></template
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import FsRouterLink from '@components/fs-router-link.vue';
import { formatUsageCategory, isUsageReferenceList, type UsageReference } from '@lib/usage-references';

// A bare list renders without a heading; an object renders one headed list per non-empty property,
// headed by its name unless `titles` says otherwise.
const { usage, titles = {} } = defineProps<{
  usage: UsageReference[] | Record<string, unknown>;
  titles?: Record<string, string>;
}>();

const groups = computed(() =>
  Array.isArray(usage)
    ? [{ key: '', title: null, items: usage }]
    : Object.entries(usage)
        .filter((entry): entry is [string, UsageReference[]] => isUsageReferenceList(entry[1]))
        .map(([key, items]) => ({ key, title: titles[key] ?? `${formatUsageCategory(key)}:`, items }))
);
</script>

<style scoped lang="scss">
@import '@styles/variables';

.usage-reference-lists {
  &__group + &__group {
    margin-top: 1.2rem;
  }

  &__title {
    font-size: 1.3rem;
    font-weight: 600;
    color: $c-dark;
    margin: 0 0 0.8rem 0;
  }

  &__list {
    margin: 0;
    padding-left: 2rem;
    max-height: 20rem;
    overflow-y: auto;
  }

  &__item {
    font-size: 1.4rem;
    color: $c-dark-light;
    margin-bottom: 0.8rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__link {
    color: $c-blue;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
